import { STORE_NAME } from '@intigral/constant';
import { Button, FontIcon, MinusCircle, Spinner, Text, toast } from '@intigral/shared';
import { log } from '@intigral/utils';
import { get } from '@intigral/utils/lib/browser-storage/localStorage';
import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { ButtonTypes } from '../../config/constants';
import { __ } from '../../translations';
import { getUserInfo } from '../../utils/user';
import { useConfigurationSections } from '../../hooks/useConfiguration';
import { IStore } from '../../interfaces/StoreTypes';
import { FontIconSize } from '@intigral/shared/components/FontIcon';
import { useUserWatchList } from '../../hooks/watchlist/useUserWatchlist';
import { useToggleWatchlistItem } from '../../hooks/watchlist/useToggleWatchlist';
import user from '../../helpers/user';
import { showGuestPromotionPopup } from '../../config/guestMode';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import { EVENT } from '../../contexts/MixpanelConstants';
import { getMixpanelLinearProperties, getMixpanelVodProperties } from '../../contexts/MixpanelHelper';

interface AddWatchlistButtonProps {
    componentType?: string;
    componentName?: any;
    type?: string;
    item?: { paId?: string; Id?: string; programType?: string; category?: string };
    onRemove?: () => void;
    onClick?: (isAdded?: boolean) => void;
    /**
     * Optional callback after the watchlist API settles (e.g. Continue Watching quick-action
     * analytics). Other carousels leave this unset.
     * `added` is true when the item was added to My List, false when removed.
     */
    onToggleResult?: (result: { validated: boolean; failureReason?: string; added?: boolean }) => void;
    watchlistButton?: boolean;
    children?: React.ReactNode;
    secondary?: boolean;
    /**
     * Continue Watching quick-action row: show "Add to watchlist" / "Remove from watchlist"
     * based on current watchlist state.
     */
    continueWatchingLabel?: boolean;
}

const AddWatchlistButton: React.FC<AddWatchlistButtonProps> = ({
    type,
    item,
    onRemove,
    onClick,
    onToggleResult,
    watchlistButton = false,
    children,
    secondary = false,
    continueWatchingLabel = false,
}) => {
    let paId = null;
    if (type === ButtonTypes.LinearItem || type === ButtonTypes.ProgramItem) paId = item?.paId;
    else paId = item?.Id;
    const [optimisticMap, setOptimisticMap] = useState<Record<string, boolean>>({});
    const geolocation = useSelector((store: IStore) => store.location);
    const { promotionProviderImages } = useConfigurationSections();
    const providers = promotionProviderImages;
    const selectedProfile = get(STORE_NAME.SELECTED_PROFILE);
    const { track }: any = useContext(MixpanelContext) || {};

    const { data } = useUserWatchList(
        selectedProfile?.profileGuid,
        getUserInfo(),
        geolocation.countryCode,
        providers,
        'en',
        ''
    );
    const toggleWatchlistItemMutation = useToggleWatchlistItem();

    const [isSaving, setIsSaving] = useState<boolean>(false);
    const isAdded = optimisticMap[paId] ?? data?.some((e: any) => e.Id === paId);

    const addOrRemoveWatchlistMutation = (e) => {
        e.stopPropagation();
        if (showGuestPromotionPopup(e)) {
            return;
        }
        e.preventDefault();
        const nextValue = !isAdded;
        setOptimisticMap((prev) => ({
            ...prev,
            [paId]: nextValue,
        }));
        toggleWatchlistItemMutation.mutate(
            {
                itemId: paId,
                category: (item?.category?.toLowerCase() ?? '') as 'linear' | 'vod',
                isAdded,
            },
            {
                onSuccess: () => {
                    toast(__(nextValue ? 'watchlist_added_toast' : 'watchlist_removed_toast'), 'dark', {
                        icon: nextValue ? (
                            <FontIcon name="check-circle" color="green" size="smaller" md="smaller" />
                        ) : (
                            <MinusCircle />
                        ),
                        notDismissible: true,
                    });
                    onToggleResult?.({ validated: true, added: nextValue });
                    // Continue Watching uses quick-action Mixpanel via onToggleResult; skip here.
                    if (!onToggleResult && !continueWatchingLabel && track && item) {
                        track(
                            nextValue ? EVENT.WATCHLIST_ACTION.ADD : EVENT.WATCHLIST_ACTION.REMOVE,
                            item.category === 'VOD'
                                ? getMixpanelVodProperties(item)
                                : getMixpanelLinearProperties(item)
                        );
                    }
                },
                onError: (error) => {
                    setOptimisticMap((prev) => {
                        const copy = { ...prev };
                        delete copy[paId];
                        return copy;
                    });
                    const failureReason =
                        (typeof error === 'string' && error) ||
                        (error as { message?: string })?.message ||
                        'Unknown error';
                    onToggleResult?.({ validated: false, failureReason, added: nextValue });
                },
            }
        );
        if (onClick) {
            onClick(isAdded);
        }
    };

    /**
     * Figma "Reminder & Favorite Logic"
     * @param {string} size Setting different icon size depending on whether it's used in promo banner
     * or in popup in carousel
     */
    const getWatchlistStatusIcon = (size: FontIconSize = 'small') =>
        isAdded ? (
            <FontIcon name="check-lg" color="orange" size={size} md={size} />
        ) : (
            <FontIcon name="plus" size={size} md={size} />
        );

    const defaultProps = {
        tabIndex: -1,
        onClick: addOrRemoveWatchlistMutation,
    };

    switch (type) {
        case ButtonTypes.VodFeaturedItem:
            return (
                <Button
                    social
                    secondary
                    onClick={addOrRemoveWatchlistMutation}
                    // loading={toggleWatchlistItemMutation.isPending}
                    data-test-id="watchlist-button"
                >
                    {children ?? __(isAdded ? 'hero_remove_from_my_list' : 'hero_add_to_my_list')}
                    {getWatchlistStatusIcon('smaller')}
                </Button>
            );
        case ButtonTypes.LinearItem:
            return (
                <span
                    id="add-watch-list"
                    data-test-id="add-watch-list"
                    className={`add-icon ${isAdded ? 'remove-icon' : ''} ${isSaving ? 'watch-loading' : ''}`}
                    {...defaultProps}
                >
                    <div>
                        <FontIcon name="plus" size="smaller" md="medium" />
                        {/* {!toggleWatchlistItemMutation.isPending ? (
                            <FontIcon name="plus" size="smaller" md="medium" />
                        ) : (
                            <div className="spinner">
                                <Spinner inline />
                            </div>
                        )} */}
                    </div>
                </span>
            );
        case ButtonTypes.VodDetails:
        case ButtonTypes.ProgramItem:
            return (
                <Button
                    primary={!watchlistButton}
                    compact
                    className={`mylist-btn ${isAdded ? 'remove-mylist' : ''}  ${isSaving ? 'watch-loading' : ''}`}
                    // loading={toggleWatchlistItemMutation.isPending}
                    data-test-id="watchlist-button"
                    {...defaultProps}
                >
                    <span>{watchlistButton ? __('mylist_button_label') : __('home_carousel_watch_list')}</span>
                    {getWatchlistStatusIcon('small')}
                </Button>
            );
        case ButtonTypes.RewindListProgramItem:
            return (
                <Button
                    primary={!watchlistButton}
                    compact
                    className={`mylist-btn ${isAdded ? 'remove-mylist' : ''}  ${isSaving ? 'watch-loading' : ''}`}
                    // loading={toggleWatchlistItemMutation.isPending}
                    data-test-id="watchlist-button"
                    {...defaultProps}
                >
                    <span>{watchlistButton ? __('mylist_button_label') : __('home_carousel_watch_list')}</span>
                    {getWatchlistStatusIcon('smaller')}
                </Button>
            );

        case ButtonTypes.VodeItem:
            return (
                <Button
                    {...{ [children || continueWatchingLabel ? 'fullWidth' : 'social']: true }}
                    secondary={secondary}
                    compact
                    // loading={toggleWatchlistItemMutation.isPending}
                    data-test-id="watchlist-button"
                    {...defaultProps}
                >
                    {continueWatchingLabel ? (
                        <>
                            {getWatchlistStatusIcon('small')}
                            <span>
                                <Text as="p" variant="text3">
                                    {__(
                                        isAdded
                                            ? 'continue_watching_remove_from_watchlist'
                                            : 'continue_watching_add_to_watchlist'
                                    )}
                                </Text>
                            </span>
                        </>
                    ) : (
                        <>
                            {children ?? null} {getWatchlistStatusIcon('smaller')}
                        </>
                    )}
                </Button>
            );
        case 'VodItem-details-overlay':
            return (
                <Button
                    plain
                    // loading={toggleWatchlistItemMutation.isPending}
                    data-test-id="watchlist-button"
                    {...defaultProps}
                >
                    <FontIcon name={isAdded ? 'close-circle' : 'plus-circle'} size="small" md="small" />
                </Button>
            );

        case ButtonTypes.EpisodeItem:
        case ButtonTypes.RewindItem:
            return (
                <Button
                    plain
                    className={`add-icon ${isAdded ? 'remove-icon' : ''} ${isSaving ? 'watch-loading' : ''}`}
                    data-test-id="watchlist-button"
                    {...defaultProps}
                >
                    <div style={{ transform: 'scale(1.1)' }}>
                        <FontIcon name={isAdded ? 'close-circle' : 'plus-circle'} size="x-large" md="size60" />
                    </div>
                </Button>
            );
        default:
            return (
                <button
                    className={`add-icon ${isAdded ? 'remove-icon' : ''} ${isSaving ? 'watch-loading' : ''}`}
                    data-test-id="watchlist-button"
                    {...defaultProps}
                >
                    <div className="circle3"> </div>
                </button>
            );
    }
};
export default AddWatchlistButton;
